import { useState, useMemo } from "react";
import { useIntl } from "react-intl";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";

import UserIcon from "../../../assets/icons/UserIcon";
import ClipboardIcon from "../../../assets/icons/ClipboardIcon";
import CheckIcon from "../../../assets/icons/CheckIcon";
import ChevronRightIcon from "../../../assets/icons/ChevronRightIcon";
import ChevronDownIcon from "../../../assets/icons/ChevronDownIcon";
import Spinner from "../../../assets/icons/Spinner";
import Notify from "../../shared/Notify";
import label from "../../../i18n/label";
import Message from "../../../i18n/Message";
import QuestionMessage from "./QuestionMessage";

import "./BuilderMessage.scss";
import { ArrowUpIcon } from "lucide-react";

const BuilderMessage = ({
  message,
  onApprovePlan,
  onRefinePlan,
  onSubmitQuestionAnswers,
  showPlanActions = false,
  isApproving = false,
}) => {
  const intl = useIntl();
  const [messageCopied, setMessageCopied] = useState(false);
  const [planExpanded, setPlanExpanded] = useState(true);
  const [refineFeedback, setRefineFeedback] = useState("");

  const isUser = message.role === "user";
  const isAssistant = message.role === "assistant";

  // Check if this is a question message
  const isQuestionMessage = useMemo(() => {
    try {
      const meta = JSON.parse(message.metadataJson || "{}");
      return meta.questionType === "askUserQuestion";
    } catch {
      return false;
    }
  }, [message.metadataJson]);

  const planApprovalState = useMemo(() => {
    if (!message?.planMd) return null;
    if (message.metadataJson) {
      try {
        const parsed = JSON.parse(message.metadataJson);
        const state = parsed?.planApproval?.state;
        if (state) {
          return state;
        }
      } catch (err) {
        return message.planApprovalNeeded ? "pending" : null;
      }
    }
    return message.planApprovalNeeded ? "pending" : null;
  }, [message?.metadataJson, message?.planMd, message?.planApprovalNeeded]);

  const planStatusLabels = {
    pending: (
      <Message
        id="builder_plan_pending"
        defaultMessage="Plan pending approval"
      />
    ),
    approved: (
      <Message id="builder_plan_approved" defaultMessage="Plan approved" />
    ),
    refined: (
      <Message id="builder_plan_refined" defaultMessage="Plan refined" />
    ),
    superseded: (
      <Message id="builder_plan_superseded" defaultMessage="Plan superseded" />
    ),
  };

  const onCopy = () => {
    if (!navigator.clipboard) {
      Notify.error(label(intl, "clipboard_copy_failed"));
      return;
    }

    navigator.clipboard.writeText(message.content).then(() => {
      setMessageCopied(true);
      setTimeout(() => {
        setMessageCopied(false);
      }, 3000);
    });
  };

  const markdownComponents = useMemo(
    () => ({
      code({ node, inline, className, children, ...props }) {
        const match = /language-(\w+)/.exec(className || "");
        return !inline && match ? (
          <SyntaxHighlighter
            style={oneLight}
            language={match[1]}
            PreTag="div"
            {...props}
          >
            {String(children).replace(/\n$/, "")}
          </SyntaxHighlighter>
        ) : (
          <code className={className} {...props}>
            {children}
          </code>
        );
      },
    }),
    []
  );

  // Delegate to QuestionMessage for question-type messages
  if (isQuestionMessage) {
    return (
      <QuestionMessage
        message={message}
        onSubmitAnswers={onSubmitQuestionAnswers}
      />
    );
  }

  return (
    <div
      className={`builder_message ${
        isUser ? "message_user" : "message_assistant"
      }`}
    >
      {/*<div className="message_header">
        <div className="message_avatar">
          {isUser ? <UserIcon /> : <span className="assistant_icon">AI</span>}
        </div>
        <div className="message_role">{isUser ? "You" : "Assistant"}</div>
        {isAssistant && (
          <button
            className="copy_btn"
            onClick={onCopy}
            title={label(intl, "copy", "Copy")}
          >
            {messageCopied ? <CheckIcon /> : <ClipboardIcon />}
          </button>
        )}
      </div>*/}

      <div className="message_body">
        {isUser ? (
          <div className="message_content">{message.content}</div>
        ) : (
          <div className="message_content markdown_content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {message.content}
            </ReactMarkdown>
          </div>
        )}

        {/* Plan section */}
        {message.planMd && (
          <div className="plan_section">
            <button
              className="plan_header"
              onClick={() => setPlanExpanded(!planExpanded)}
            >
              {planExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
              <span className="plan_title">
                <Message id="builder_plan_created" />
              </span>
              {planApprovalState && (
                <span
                  className={`plan_status plan_status_${planApprovalState}`}
                >
                  {planStatusLabels[planApprovalState] || planApprovalState}
                </span>
              )}
            </button>
            {planExpanded && (
              <div className="plan_content markdown_content">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={markdownComponents}
                >
                  {message.planMd}
                </ReactMarkdown>
              </div>
            )}
          </div>
        )}

        {/* Plan approval actions */}
        {message.planApprovalNeeded && showPlanActions && (
          <div className="plan_approval_actions">
            <div className="approval_content">
              <button
                className="btn btn-primary approve_btn"
                onClick={onApprovePlan}
                disabled={isApproving}
              >
                {isApproving ? (
                  <>
                    <Spinner size={20} />
                    <Message
                      id="builder_approving"
                      defaultMessage="Approving..."
                    />
                  </>
                ) : (
                  <>
                    <CheckIcon size={20} color="#fff" strokeWidth="2" />
                    <Message id="builder_approve_plan" />
                  </>
                )}
              </button>
              <div className="approval_hint">
                <Message id="builder_approval_or_refine" />
              </div>
              <form
                className="refine_form"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (refineFeedback.trim()) {
                    onRefinePlan(refineFeedback.trim());
                    setRefineFeedback("");
                  }
                }}
              >
                <input
                  type="text"
                  className="refine_input"
                  placeholder={label(intl, "builder_refine_placeholder")}
                  value={refineFeedback}
                  onChange={(e) => setRefineFeedback(e.target.value)}
                />
                <button
                  type="submit"
                  className="refine_send_btn"
                  disabled={!refineFeedback.trim()}
                  title={label(intl, "send", "Send")}
                >
                  <ArrowUpIcon size={20} color="#fff" />
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BuilderMessage;
